import { useEffect } from 'react'
import { getCookie } from '@/features/shared/utils/cookies'
import { useMutationSendOptOutRequest } from '@/features/account/services/DataPrivacy/mutations'
import {
  optOutBrowserCookieIsActive,
  optOutBrowserSignalIsActive,
  optOutRestrictionsCookieIsActive,
  userIsDoNotShareSellTA,
} from '@/features/account/services/DataPrivacy/utils'
import { fetchHandleOptOutCookies } from '@/features/account/services/DataPrivacy/fetchers'
import { type RegulationCategoryStateAndReason } from '@/features/account/services/DataPrivacy/types'
import { SHIPT_RESTRICTION_BROWSER_COOKIE } from '@shared/constants/dataRights'
import { useUser } from '@/features/account/services/User/hooks'
import { useQueryOptOutRestrictions } from '@/features/account/services/DataPrivacy/queries'

declare global {
  // Add globalPrivacyControl to the global Navigator type
  // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/globalPrivacyControl
  interface Navigator {
    globalPrivacyControl?: boolean
  }
  // Add the type for the global Window variable that Data Science uses to block
  // Google Tag Manager scripts from loading.
  interface Window {
    SHIPT_DO_NOT_SHARE_SELL_TA?: boolean
  }
}

export const useDataRights = () => {
  const user = useUser()
  const { data: optOutRestriction } = useQueryOptOutRestrictions()
  const { mutate: sendOptOutRequest } = useMutationSendOptOutRequest()

  useEffect(() => {
    // If the user has opted out, set the global SHIPT_PRIVACY_BROWSER_COOKIE variable
    // to true so that the Google Tag Manager scripts can be blocked.
    if (userIsDoNotShareSellTA()) {
      window.SHIPT_DO_NOT_SHARE_SELL_TA = true
    }

    // User must be authenticated to continue
    if (!user?.id) return

    if (user.do_not_share_sell_ta) {
      // Check the opt-out user preference from the response. If the preference is
      // set and the browser cookie isn't set, then we want to invoke the opt-out
      // API endpoint to set the cookies.
      if (!optOutBrowserCookieIsActive()) fetchHandleOptOutCookies()

      // User is already fully opted out, nothing left to do
      return
    }

    // If the user has the do not sell, share, or targeted advertising browser signal
    // enabled, but their do_not_share_sell_ta user preference returned empty or false;
    // we need to submit to the identity workflow that they are using the browser signal.
    if (optOutBrowserSignalIsActive()) {
      // Make request to identity workflow indicating user opted out through browser signal.
      sendOptOutRequest({ request_source: 'web-signal' })
    }

    // If the user has the do not sell, share, or targeted advertising browser cookie,
    // but their do_not_share_sell_ta user preference returned empty or false;
    // we need to submit to the identity workflow that they submitted the opt out form.
    if (optOutBrowserCookieIsActive()) {
      // Make request to identity workflow indicating user opted out through the web form.
      sendOptOutRequest({ request_source: 'web-form' })
      return
    }

    // If the user has a confirmed restriction cookie, notify the identity workflow
    // that they should be opted out due to WA data rights.
    if (optOutRestrictionsCookieIsActive()) {
      const restrictionCookieValues: RegulationCategoryStateAndReason = getCookie(
        SHIPT_RESTRICTION_BROWSER_COOKIE,
      )
      sendOptOutRequest({
        request_source: 'web-restriction',
        ...restrictionCookieValues,
      })
      return
    }

    // If the user needs to be opted out based on the opt out restrictions data, notify
    // the identity workflow that they should be opted out due to WA data rights.
    if (optOutRestriction) {
      sendOptOutRequest({
        request_source: 'web-restriction',
        regulation_state: optOutRestriction.regulation_state,
        regulation_reason: optOutRestriction.regulation_reason,
        regulation_category: optOutRestriction.regulation_category,
      })
    }
  }, [sendOptOutRequest, user?.id, user?.do_not_share_sell_ta, optOutRestriction])
}
